import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useI18n, LangSwitch } from "@/lib/i18n";
import { Fingerprint, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";

export default function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot", { email: email.toLowerCase().trim() });
      setSent(true);
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#F4F4F0]">
      <div className="hidden md:flex relative bg-black text-white flex-col p-10 justify-between overflow-hidden">
        <div className="relative">
          <div className="flex items-center gap-3">
            <Fingerprint size={36} weight="bold" />
            <div className="font-display font-black text-3xl tracking-tighter">ATTENDIO</div>
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mt-2 opacity-80">{t("brand_line")}</div>
        </div>
        <div className="relative">
          <div className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.9]">
            {t("forgot_pw_title")}
          </div>
        </div>
        <div />
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="forgot-form" className="w-full max-w-md brut-border bg-white p-8 brut-shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-display font-black text-4xl tracking-tighter flex items-center gap-2">
                <EnvelopeSimple size={28} weight="bold" /> {t("forgot_pw_title")}
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mt-1">
                {t("forgot_pw_hint")}
              </div>
            </div>
            <LangSwitch />
          </div>

          {sent ? (
            <div data-testid="forgot-sent" className="brut-border bg-[#FFCC00] text-black p-4 font-bold">
              {t("forgot_pw_sent")}
            </div>
          ) : (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("email")}</label>
              <input
                data-testid="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full brut-border p-3 mb-2 focus:outline-none focus:border-[#0047FF] focus:shadow-[4px_4px_0_0_#0047FF] transition-all"
                required
              />
              {error && (
                <div data-testid="forgot-error" className="brut-border bg-[#FF3333] text-white p-2 text-sm font-bold mb-2">
                  {error}
                </div>
              )}
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading}
                className="w-full mt-4 bg-black text-white brut-border brut-shadow brut-hover p-4 font-display font-black text-xl tracking-tighter uppercase flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "…" : t("forgot_pw_submit")} <ArrowRight size={20} weight="bold" />
              </button>
            </>
          )}

          <div className="mt-6 text-xs">
            <Link to="/login" data-testid="back-to-login" className="underline font-bold">← {t("back_to_login")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
